import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LabelService } from '../../../services/label.service';
import { MatIconModule } from '@angular/material/icon';
import { InvoicePatient, InvoicePrivateClient } from '../../../models/invoices';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CountryService } from '../country.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-invoices-private-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSelectModule,
    FormsModule,
  ],
  templateUrl: './invoices-private-panel.component.html',
  styleUrl: './invoices-private-panel.component.scss',
})
export class InvoicesPrivatePanelComponent implements OnInit {
  @Input() client!: InvoicePrivateClient;
  @Input() patients: InvoicePatient[] = [];
  @Input() newPatientPanelTitle: string = 'Nuovo Cliente Privato';
  @Output() closePanel: EventEmitter<void> = new EventEmitter<void>();
  @Output() createClient = new EventEmitter<InvoicePrivateClient>();
  @Output() updateClient = new EventEmitter<InvoicePrivateClient>();

  isEditClient: boolean = false;
  clientForm: FormGroup;
  selectedPatient: InvoicePatient | null = null;
  showPatientsList: boolean = false;
  countries: { name: string; code: string }[] = [];
  filteredCountries: { name: string; code: string }[] = [];
  filteredPatients: InvoicePatient[] = [];
  patientSearchText: string = '';

  constructor(
    private fb: FormBuilder,
    public labelService: LabelService,
    private countryService: CountryService
  ) {
    // this.countries = this.countryService.getCountries();
    this.countries = [{ name: 'Italia', code: 'IT' }];
    this.filteredCountries = this.countries;
    this.clientForm = this.fb.group({
      is_patient: [false],
      client_first_name: ['', Validators.required],
      client_last_name: ['', Validators.required],
      client_email: ['', [Validators.email]],
      client_number: [''],
      client_tax_code: ['', Validators.required],
      client_country: ['', Validators.required],
      client_city: ['', Validators.required],
      client_province: ['', Validators.required],
      client_street_address: ['', Validators.required],
      client_street_number: ['', Validators.required],
      client_zip_code: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.filteredPatients = this.sortPatients([...this.patients]);

    if (this.client && Object.keys(this.client).length > 0) {
      if (this.client.id !== '') {
        this.isEditClient = true;
      }

      // Create a copy of the client data with the country name instead of code
      const clientWithCountryName = {
        ...this.client,
        client_country: this.countryService.getNameFromCode(
          this.client.client_country
        ),
      };

      this.clientForm.patchValue(clientWithCountryName);
      this.clientForm
        .get('is_patient')
        ?.setValue(
          this.client.patient_id && this.client.patient_id !== '' ? true : false
        );

      if (
        this.client.patient_id &&
        this.client.patient_first_name &&
        this.client.patient_last_name
      ) {
        this.selectedPatient = {
          id: this.client.patient_id,
          first_name: this.client.patient_first_name,
          last_name: this.client.patient_last_name,
          tax_code: this.client.client_tax_code,
          email: this.client.client_email,
          number: this.client.client_number || '',
        };
        this.patientSearchText = `${this.client.patient_first_name} ${this.client.patient_last_name}`;
      }
    }
  }

  filterPatients(searchText: string) {
    this.patientSearchText = searchText;
    if (!searchText || searchText.trim() === '') {
      this.filteredPatients = this.sortPatients([...this.patients]);
    } else {
      this.filteredPatients = this.sortPatients(
        this.patients.filter((patient) =>
          `${patient.first_name} ${patient.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        )
      );
    }
  }

  sortPatients(patients: InvoicePatient[]): InvoicePatient[] {
    return patients.sort((a, b) => a.last_name.localeCompare(b.last_name));
  }

  onInputClick(event: Event) {
    event.stopPropagation();
    this.showPatientsList = true;
    this.filterPatients(this.patientSearchText || '');
  }

  selectPatient(patient: InvoicePatient) {
    this.selectedPatient = patient;
    this.patientSearchText = `${patient.first_name} ${patient.last_name}`;
    this.showPatientsList = false;
    this.clientForm.patchValue({
      client_first_name: patient.first_name,
      client_last_name: patient.last_name,
      client_email: patient.email,
      client_number: patient.number,
      client_tax_code: patient.tax_code,
    });
  }

  togglePatientsList() {
    this.showPatientsList = !this.showPatientsList;
    if (this.showPatientsList) {
      this.filterPatients(this.patientSearchText || '');
    }
  }

  onCancel() {
    this.closePanel.emit();
  }

  onConfirm() {
    if (this.clientForm.valid) {
      const formValue = this.clientForm.value;
      const countryCode = this.countryService.getCodeFromName(
        formValue.client_country
      );
      const updatedClient: InvoicePrivateClient = {
        ...formValue,
        client_country: countryCode,
        patient_id: `${this.selectedPatient?.id}`,
        patient_first_name: this.selectedPatient?.first_name,
        patient_last_name: this.selectedPatient?.last_name,
        id: this.client?.id || '',
      };
      if (this.isEditClient) {
        this.updateClient.emit(updatedClient);
      } else {
        this.createClient.emit(updatedClient);
      }
    }
  }

  filterCountries(event: any) {
    const searchText = event.target.value.toLowerCase();
    this.filteredCountries = this.countries.filter((country) =>
      country.name.toLowerCase().includes(searchText)
    );
  }
}
